// Create a dedicated service file for Appwrite follow operations
// appwriteFollowService.js

import { Client, Databases, Query, ID } from "appwrite";

// Initialize Appwrite client
const client = new Client();
client
  .setEndpoint(process.env.REACT_APP_APPWRITE_ENDPOINT)
  .setProject(process.env.REACT_APP_APPWRITE_PROJECT_ID);

const databases = new Databases(client);

// Constants
const DATABASE_ID = "681f7caf0033c79e0bc0";
const FOLLOWERS_COLLECTION_ID = "681f7e8e0028ee01df31";
const FOLLOW_REQUESTS_COLLECTION_ID = "681f7d25000d9b5e446c";
const NOTIFICATION_ID = "681f845c0032fe308afb";


// Send a follow request
export const sendFollowRequest = async (requesterId, userId) => {
  try {
    // Check if there's already a follow relationship
    const existingFollowResult = await databases.listDocuments(
      DATABASE_ID,
      FOLLOWERS_COLLECTION_ID,
      [
        Query.equal("followerId", requesterId),
        Query.equal("followedId", userId)
      ]
    );
    
    if (existingFollowResult.documents.length > 0) {
      // Already following - unfollow by deleting the document
      await databases.deleteDocument(
        DATABASE_ID, 
        FOLLOWERS_COLLECTION_ID, 
        existingFollowResult.documents[0].$id
      );
      return { isFollowing: false, isPending: false };
    }
    
    // Check if there's already a pending request
    const existingRequestResult = await databases.listDocuments(
      DATABASE_ID,
      FOLLOW_REQUESTS_COLLECTION_ID,
      [
        Query.equal("requesterId", requesterId),
        Query.equal("userId", userId),
        Query.equal("status", "pending")
      ]
    );
    
    if (existingRequestResult.documents.length > 0) {
      // Cancel pending request by deleting it
      await databases.deleteDocument(
        DATABASE_ID, 
        FOLLOW_REQUESTS_COLLECTION_ID, 
        existingRequestResult.documents[0].$id
      );
      return { isFollowing: false, isPending: false };
    }
    
    // Create new follow request
    await databases.createDocument(
      DATABASE_ID,
      FOLLOW_REQUESTS_COLLECTION_ID,
      ID.unique(),
      {
        requesterId: requesterId,
        userId: userId,
        status: "pending",
        timestamp: new Date().toISOString()
      }
    );
    
    return { isFollowing: false, isPending: true };
  } catch (error) {
    // console.error("Error in sendFollowRequest:", error);
    throw error;
  }
};

// Get follow status between users
export const getFollowStatus = async (requesterId, userId) => {
  try {
    // Check if already following
    const existingFollowResult = await databases.listDocuments(
      DATABASE_ID,
      FOLLOWERS_COLLECTION_ID,
      [
        Query.equal("followerId", requesterId),
        Query.equal("followedId", userId)
      ]
    );
    
    if (existingFollowResult.documents.length > 0) {
      return { isFollower: true, isPending: false };
    }
    
    // Check if there's a pending request
    const existingRequestResult = await databases.listDocuments(
      DATABASE_ID,
      FOLLOW_REQUESTS_COLLECTION_ID,
      [
        Query.equal("requesterId", requesterId),
        Query.equal("userId", userId),
        Query.equal("status", "pending")
      ]
    );
    
    return { 
      isFollower: false, 
      isPending: existingRequestResult.documents.length > 0 
    };
  } catch (error) {
    // console.error("Error in getFollowStatus:", error);
    throw error;
  }
};

// Get pending follow requests for a user
export const getPendingFollowRequests = async (userId) => {
  try {
    const result = await databases.listDocuments(
      DATABASE_ID,
      FOLLOW_REQUESTS_COLLECTION_ID,
      [
        Query.equal("userId", userId),
        Query.equal("status", "pending")
      ]
    );
    
    return result.documents;
  } catch (error) {
    // console.error("Error in getPendingFollowRequests:", error);
    throw error;
  }
};

// Accept follow request
export const acceptFollowRequest = async (userId, requesterId) => {
  try {
    // Find the request
    const requestResult = await databases.listDocuments(
      DATABASE_ID,
      FOLLOW_REQUESTS_COLLECTION_ID,
      [
        Query.equal("requesterId", requesterId),
        Query.equal("userId", userId),
        Query.equal("status", "pending")
      ]
    );
    
    if (requestResult.documents.length === 0) {
      throw new Error("Follow request not found");
    }
    
    const requestDoc = requestResult.documents[0];
    
    // Create follower relationship
    await databases.createDocument(
      DATABASE_ID,
      FOLLOWERS_COLLECTION_ID,
      ID.unique(),
      {
        followerId: requesterId,
        followedId: userId,
        timestamp: new Date().toISOString()
      }
    );
    
    // Update request status or delete it
    await databases.deleteDocument(
      DATABASE_ID,
      FOLLOW_REQUESTS_COLLECTION_ID,
      requestDoc.$id
    );
    
    return { success: true };
  } catch (error) {
    // console.error("Error in acceptFollowRequest:", error);
    throw error;
  }
};

// Reject follow request
export const rejectFollowRequest = async (userId, requesterId) => {
  try {
    // Find the request
    const requestResult = await databases.listDocuments(
      DATABASE_ID,
      FOLLOW_REQUESTS_COLLECTION_ID,
      [
        Query.equal("requesterId", requesterId),
        Query.equal("userId", userId),
        Query.equal("status", "pending")
      ]
    );
    
    if (requestResult.documents.length === 0) {
      throw new Error("Follow request not found");
    }
    
    // Delete the request
    await databases.deleteDocument(
      DATABASE_ID,
      FOLLOW_REQUESTS_COLLECTION_ID,
      requestResult.documents[0].$id
    );
    
    return { success: true };
  } catch (error) {
    // console.error("Error in rejectFollowRequest:", error);
    throw error;
  }
};

// Create follow request notification
// Create follow request notification
export const createFollowRequestNotification = async (
    receiverId,
    senderId,
    senderName,
    senderPicturePath
  ) => {
    try {
      // Create notification in Appwrite
      await databases.createDocument(
        DATABASE_ID,
        NOTIFICATION_ID,
        ID.unique(),
        {
          receiverId: receiverId,
          senderId: senderId,
          senderName: senderName,
          senderPicturePath: senderPicturePath,
          type: 'follow_request',
          content: 'wants to follow you',
          isRead: false,
          createdAt: new Date().toISOString(),
          postId: null  // Add this required field, set to null for follow notifications
        }
      );
      
      // console.log(`Follow request notification created for user ${receiverId}`);
    } catch (error) {
      // console.error("Error creating follow request notification:", error);
      throw error;
    }
  };

// Create follow accepted notification
// Create follow accepted notification
export const createFollowAcceptedNotification = async (
    receiverId,
    senderId,
    senderName,
    senderPicturePath
  ) => {
    try {
      // Create notification in Appwrite
      await databases.createDocument(
        DATABASE_ID,
        NOTIFICATION_ID,
        ID.unique(),
        {
          receiverId: receiverId,
          senderId: senderId,
          senderName: senderName,
          senderPicturePath: senderPicturePath,
          type: 'follow_accepted',
          content: 'accepted your follow request',
          isRead: false,
          createdAt: new Date().toISOString(),
          postId: null  // Add this required field, set to null for follow notifications
        }
      );
      
      // console.log(`Follow accepted notification created for user ${receiverId}`);
    } catch (error) {
      // console.error("Error creating follow accepted notification:", error);
      throw error;
    }
  };

  // Add this function to appwriteFollowService.js to check the schema

// Function to fetch and log the notification collection schema
export const checkNotificationSchema = async () => {
    try {
      // Fetch the collection details to see its schema
      const collection = await client.database.getCollection(
        DATABASE_ID,
        NOTIFICATION_ID
      );
      
      // console.log("Notification Collection Schema:", collection);
      
      // Return the collection details for inspection
      return collection;
    } catch (error) {
      // console.error("Error fetching notification schema:", error);
      throw error;
    }
  };
  
  // Alternative approach - modify the notification creation functions to handle API failures
  export const createFollowRequestNotificationSafe = async (
    receiverId,
    senderId,
    senderName,
    senderPicturePath
  ) => {
    try {
      // Try creating with our best guess at the required fields
      const notificationData = {
        receiverId: receiverId,
        senderId: senderId,
        senderName: senderName,
        senderPicturePath: senderPicturePath,
        type: 'follow_request',
        content: 'wants to follow you',
        isRead: false,
        createdAt: new Date().toISOString(),
        postId: "no-post"  // Placeholder value
      };
      
      // Log what we're trying to send
      // console.log("Attempting to create notification with data:", notificationData);
      
      // Create notification in Appwrite
      const response = await databases.createDocument(
        DATABASE_ID,
        NOTIFICATION_ID,
        ID.unique(),
        notificationData
      );
      
      // console.log(`Follow request notification created for user ${receiverId}`, response);
      return response;
    } catch (error) {
      // If there's an error, let's extract the specific field causing the issue
      // console.error("Error creating follow request notification:", error);
      // console.log("Error message:", error.message);
      // console.log("Error type:", error.type);
      // console.log("Error code:", error.code);
      
      // Log more details about the error if available
      if (error.response) {
        // console.log("Error response:", error.response);
      }
      
      // Don't throw the error - we'll still consider the follow request action successful
      // even if the notification creation failed
      return null;
    }
  };

  // Add this to appwriteFollowService.js

// Safe version of createFollowAcceptedNotification that handles errors gracefully
export const createFollowAcceptedNotificationSafe = async (
    receiverId,
    senderId,
    senderName,
    senderPicturePath
  ) => {
    try {
      // Try creating with our best guess at the required fields
      const notificationData = {
        receiverId: receiverId,
        senderId: senderId,
        senderName: senderName,
        senderPicturePath: senderPicturePath,
        type: 'follow_accepted',
        content: 'accepted your follow request',
        isRead: false,
        createdAt: new Date().toISOString(),
        postId: "no-post"  // Placeholder value
      };
      
      // Log what we're trying to send
      // console.log("Attempting to create follow accepted notification with data:", notificationData);
      
      // Create notification in Appwrite
      const response = await databases.createDocument(
        DATABASE_ID,
        NOTIFICATION_ID,
        ID.unique(),
        notificationData
      );
      
      // console.log(`Follow accepted notification created for user ${receiverId}`, response);
      return response;
    } catch (error) {
      // If there's an error, let's extract the specific field causing the issue
      // console.error("Error creating follow accepted notification:", error);
      // console.log("Error message:", error.message);
      // console.log("Error type:", error.type);
      // console.log("Error code:", error.code);
      
      // Log more details about the error if available
      if (error.response) {
        // console.log("Error response:", error.response);
      }
      
      // Don't throw the error - we'll still consider the follow accept action successful
      // even if the notification creation failed
      return null;
    }
  };

  // Add this function to appwriteFollowService.js

export const createPostUploadNotificationForFollowers = async (
  uploaderId, // ID of user who uploaded the post
  uploaderName, // Name of the user who uploaded
  uploaderPicturePath, // Profile picture of uploader
  postId // ID of the newly uploaded post
) => {
  try {
    // Get all followers of the uploader
    const followersResponse = await databases.listDocuments(
      DATABASE_ID,
      FOLLOWERS_COLLECTION_ID,
      [Query.equal('followedId', uploaderId)]
    );
    
    // Create notifications for each follower
    const promises = followersResponse.documents.map(async (follower) => {
      return databases.createDocument(
        DATABASE_ID,
        NOTIFICATION_ID,
        ID.unique(),
        {
          receiverId: follower.followerId, // The follower gets the notification
          senderId: uploaderId, // The uploader is the sender
          senderName: uploaderName,
          senderPicturePath: uploaderPicturePath,
          type: 'post_upload', // New notification type
          postId: postId,
          isRead: false,
          createdAt: new Date().toISOString()
        }
      );
    });
    
    await Promise.all(promises);
    return { success: true };
  } catch (error) {
    // console.error("Error creating post upload notifications:", error);
    return { success: false, error };
  }
};

// Also create a safe version like you did for follow requests
export const createPostUploadNotificationForFollowersSafe = async (
  uploaderId,
  uploaderName,
  uploaderPicturePath,
  postId
) => {
  try {
    return await createPostUploadNotificationForFollowers(
      uploaderId,
      uploaderName,
      uploaderPicturePath,
      postId
    );
  } catch (error) {
    // console.error("Failed to create post upload notifications:", error);
    return { success: false };
  }
};
